import React from 'react'
import { Box, Stack, Typography } from '@mui/material'

import { SMOOTH_ICON_SX } from './styles'

export const WidgetIcon = ({
  icon,
  backgroundColor = '#3D3D3D',
  progress,
  showProgress = false,
  progressColor = '#92BC62',
  progressTrackColor = '#F7F7F7',
  progressRemainderColor
}) => {
  const hasProgress = showProgress || typeof progress === 'number'
  const safeProgress = Number.isFinite(Number(progress)) ? Math.max(0, Math.min(Number(progress), 100)) : 0
  const progressSize = 42
  const progressStroke = 4
  const progressRadius = 18
  const progressCircumference = 2 * Math.PI * progressRadius
  const hasRemainderProgress = Boolean(progressRemainderColor)
  const progressGap = hasRemainderProgress && safeProgress > 0 && safeProgress < 100 ? 5 : 0
  const progressLength = progressCircumference * (safeProgress / 100)
  const progressStartOffset = progressGap / 2
  const visibleProgressLength = Math.max(0, progressLength - progressGap)
  const remainderLength = hasRemainderProgress && safeProgress < 100
    ? Math.max(0, progressCircumference - progressLength - progressGap)
    : 0
  const remainderOffset = -(progressStartOffset + visibleProgressLength + progressGap)

  return (
    <Box
      sx={{
        width: hasProgress ? progressSize : 28,
        height: hasProgress ? progressSize : 28,
        borderRadius: '50%',
        backgroundColor: hasProgress ? '#fff' : backgroundColor,
        border: hasProgress ? '1px solid #EFEFEF' : 0,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative'
      }}
    >
      {hasProgress && (
        <Box component="svg" viewBox={`0 0 ${progressSize} ${progressSize}`} sx={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
          <circle
            cx={progressSize / 2}
            cy={progressSize / 2}
            r={progressRadius}
            fill="none"
            stroke={hasRemainderProgress ? '#fff' : progressTrackColor}
            strokeWidth={progressStroke}
          />
          {remainderLength > 0 && (
            <circle
              cx={progressSize / 2}
              cy={progressSize / 2}
              r={progressRadius}
              fill="none"
              stroke={progressRemainderColor}
              strokeWidth={progressStroke}
              strokeLinecap="round"
              strokeDasharray={`${remainderLength} ${progressCircumference}`}
              strokeDashoffset={remainderOffset}
            />
          )}
          {safeProgress > 0 && (
            <circle
              cx={progressSize / 2}
              cy={progressSize / 2}
              r={progressRadius}
              fill="none"
              stroke={progressColor}
              strokeWidth={progressStroke}
              strokeLinecap="round"
              strokeDasharray={`${visibleProgressLength} ${progressCircumference}`}
              strokeDashoffset={-progressStartOffset}
            />
          )}
        </Box>
      )}
      <Box
        component="span"
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transform: 'translateZ(0)'
        }}
      >
        <Box component="i" className={icon} sx={{ fontSize: 12, ...SMOOTH_ICON_SX }} />
      </Box>
    </Box>
  )
}

export const MetricValue = ({ value, unit }) => (
  <Typography component="span" sx={{ color: '#464646', fontSize: 14, fontWeight: 600, lineHeight: '16px' }}>
    {value}
    {unit && (
      <Typography component="span" sx={{ color: '#989898', fontSize: 10, fontWeight: 500, ml: 0.35 }}>
        {unit}
      </Typography>
    )}
  </Typography>
)

export const MetricWidget = ({ title, value, unit, icon, progress, iconColor, showProgress, progressColor, progressTrackColor, progressRemainderColor, children, sx }) => (
  <Box
    sx={{
      minHeight: 72,
      border: '1px solid #EFEFEF',
      borderRadius: '32px',
      backgroundColor: '#fff',
      px: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.25,
      minWidth: 0,
      ...sx
    }}
  >
    <Stack gap={0.25} minWidth={0}>
      <Typography sx={{ color: '#464646', fontSize: 12, fontWeight: 500, lineHeight: '14px' }}>{title}</Typography>
      {children || <MetricValue value={value ?? '-'} unit={value == null ? null : unit} />}
    </Stack>
    <WidgetIcon
      icon={icon}
      backgroundColor={iconColor}
      progress={progress}
      showProgress={showProgress}
      progressColor={progressColor}
      progressTrackColor={progressTrackColor}
      progressRemainderColor={progressRemainderColor}
    />
  </Box>
)

export const FractionValue = ({ current, total, currentColor }) => (
  <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, lineHeight: '16px', color: '#464646' }}>
    <Typography component="span" sx={{ color: currentColor || '#464646', fontSize: 'inherit', fontWeight: 'inherit' }}>
      {current ?? '-'}
    </Typography>
    <Typography component="span" sx={{ color: '#989898', fontSize: 10, fontWeight: 500, ml: 0.35 }}>
      / {total ?? '-'}
    </Typography>
  </Typography>
)

export const CenteredIndicatorWidget = ({ title, icon, children, sx }) => (
  <Box
    sx={{
      minHeight: 112,
      border: '1px solid #EFEFEF',
      borderRadius: '24px',
      backgroundColor: '#fff',
      px: 2,
      py: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      minWidth: 0,
      ...sx
    }}
  >
    <Box component="i" className={icon} sx={{ color: '#3D3D3D', fontSize: 14, ...SMOOTH_ICON_SX }} />
    <Typography sx={{ color: '#464646', fontSize: 10, fontWeight: 700, lineHeight: '12px', textAlign: 'center', textTransform: 'uppercase' }}>
      {title}
    </Typography>
    {children}
  </Box>
)
